import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, CircularProgress, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Alert, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Select, FormControl,
  InputLabel, IconButton, Tooltip, Chip
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import { useAuth } from '../../hooks/useAuth';
import { apiService } from '../../services/api';
import { LABELS } from '../../utils/labels';
import VehicleConflictDialog from './VehicleConflictDialog';

const statusColor = {
  active: 'success',
  inactive: 'warning',
  sold: 'error',
};

const DriverRegisterList = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [vehicles, setVehicles] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(user?.branch || '');

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    number: '', license_plate: '', vin: '', make: '', model: '', year: '',
    status: 'active', branch: ''
  });

  const [conflictDialogOpen, setConflictDialogOpen] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [pendingUpdate, setPendingUpdate] = useState(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const branchToLoad = selectedBranch || user?.branch;
        const data = await apiService.getVehiclesByBranch(branchToLoad);
        if (!mounted) return;
        setVehicles(data.vehicles || []);
      } catch (e) {
        if (!mounted) return;
        setError(typeof e === 'string' ? e : (e?.message || 'Failed to load vehicles'));
      } finally {
        if (mounted) setLoading(false);
      }
    };
    if (user?.is_admin) {
      load();
      (async () => {
        try {
          const allBranches = await apiService.getBranches();
          if (!mounted) return;
          setBranches(allBranches || []);
        } catch (_) { /* ignore */ }
      })();
    } else {
      setLoading(false);
    }
    return () => { mounted = false; };
  }, [user, selectedBranch]);

  const refresh = async () => {
    try {
      const branchToLoad = selectedBranch || user?.branch;
      const data = await apiService.getVehiclesByBranch(branchToLoad);
      setVehicles(data.vehicles || []);
    } catch (e) {
      setError(typeof e === 'string' ? e : (e?.message || 'Failed to load vehicles'));
    }
  };

  const openAdd = () => {
    setForm({
      number: '', license_plate: '', vin: '', make: '', model: '', year: '',
      status: 'active', branch: selectedBranch || user?.branch || ''
    });
    setIsAddOpen(true);
  };

  const submitAdd = async () => {
    if (!form.number || !form.branch) {
      setError('Unit Number and Branch are required');
      return;
    }
    setSaving(true);
    try {
      const duplicateCheckData = {
        license_plate: form.license_plate || null,
        vin: form.vin || null,
        unit_number: form.number
      };
      const duplicateCheck = await apiService.checkVehicleDuplicates('temp', duplicateCheckData);
      if (duplicateCheck.conflicts && duplicateCheck.conflicts.length > 0) {
        setPendingUpdate({
          updateData: { ...form, year: form.year ? Number(form.year) : null },
          isAdd: true
        });
        setConflicts(duplicateCheck.conflicts);
        setConflictDialogOpen(true);
        setSaving(false);
        return;
      }
      await apiService.createVehicle({ ...form, year: form.year ? Number(form.year) : null });
      setIsAddOpen(false);
      await refresh();
    } catch (e) {
      setError(typeof e === 'string' ? e : (e?.message || 'Failed to add vehicle'));
    } finally {
      setSaving(false);
    }
  };

  const handleConflictReassign = async (conflictsToReassign) => {
    setSaving(true);
    try {
      const { updateData } = pendingUpdate;
      const firstConflict = conflictsToReassign[0];
      if (firstConflict) {
        await apiService.reassignVehicleData(updateData.number, {
          conflicts: conflictsToReassign,
          sourceNumber: firstConflict.conflictingVehicle.number
        });
      }
      await apiService.createVehicle(updateData);
      setIsAddOpen(false);
      setConflictDialogOpen(false);
      setPendingUpdate(null);
      setConflicts([]);
      await refresh();
    } catch (e) {
      setError(typeof e === 'string' ? e : (e?.message || 'Failed to reassign data'));
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    try {
      const base = process.env.REACT_APP_API_BASE_URL || 'https://api.mascottcompliance.com/api';
      const token = localStorage.getItem('token');
      const response = await fetch(`${base}/vehicles/register/export`, {
        headers: { Authorization: token ? `Bearer ${token}` : '' }
      });
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const link = document.createElement('a');
      const url = window.URL.createObjectURL(blob);
      link.href = url;
      link.download = 'driver-register-all-branches.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (_) {
      setError('Failed to export register');
    }
  };

  if (!user?.is_admin) {
    return (
      <Box sx={{ maxWidth: 900, mx: 'auto', p: 3 }}>
        <Alert severity="warning">Admin access required.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: 3 }}>
      {/* Header toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>Driver Register</Typography>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="branch-select-label">Branch</InputLabel>
          <Select
            labelId="branch-select-label"
            value={selectedBranch}
            label="Branch"
            onChange={(e) => setSelectedBranch(e.target.value)}
          >
            {branches.length === 0 && (
              <MenuItem value={selectedBranch} disabled>Loading branches...</MenuItem>
            )}
            {branches.map((b) => (
              <MenuItem key={b.name} value={b.name}>{b.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
          Add Vehicle
        </Button>
        <Tooltip title="Export all branches to Excel">
          <IconButton onClick={handleExport}>
            <DownloadIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Unit Number</TableCell>
                  <TableCell>Assigned Driver</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {vehicles.map((v) => (
                  <TableRow
                    key={v.number}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/admin/driver-register/${encodeURIComponent(v.number)}`)}
                  >
                    <TableCell sx={{ fontWeight: 600 }}>{v.number}</TableCell>
                    <TableCell>
                      {v.assigned_user_username || (
                        <Chip label="Unassigned" size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={v.status || 'active'}
                        size="small"
                        color={statusColor[v.status] || 'default'}
                      />
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="View details">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/admin/driver-register/${encodeURIComponent(v.number)}`)}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit vehicle">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/admin/driver-register/${encodeURIComponent(v.number)}/edit`)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {vehicles.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No vehicles found for this branch.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Add Vehicle Dialog */}
      <Dialog open={isAddOpen} onClose={() => setIsAddOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add Vehicle</DialogTitle>
        <DialogContent>
          <TextField margin="normal" label={LABELS.unitNumber} fullWidth
            value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} />
          <TextField margin="normal" label={LABELS.licensePlate} fullWidth
            value={form.license_plate} onChange={(e) => setForm({ ...form, license_plate: e.target.value })} />
          <TextField margin="normal" label={LABELS.vin} fullWidth
            value={form.vin} onChange={(e) => setForm({ ...form, vin: e.target.value })} />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField margin="normal" label={LABELS.make} fullWidth
              value={form.make} onChange={(e) => setForm({ ...form, make: e.target.value })} />
            <TextField margin="normal" label={LABELS.model} fullWidth
              value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} />
            <TextField margin="normal" label={LABELS.year} type="number" fullWidth
              value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel id="add-status-label">{LABELS.status}</InputLabel>
              <Select labelId="add-status-label" label="Status" value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="add-branch-label">Branch</InputLabel>
              <Select labelId="add-branch-label" label="Branch" value={form.branch}
                onChange={(e) => setForm({ ...form, branch: e.target.value })}>
                {branches.map((b) => (
                  <MenuItem key={b.name} value={b.name}>{b.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsAddOpen(false)}>Cancel</Button>
          <Button onClick={submitAdd} variant="contained" disabled={saving}>
            {saving ? 'Saving...' : 'Add Vehicle'}
          </Button>
        </DialogActions>
      </Dialog>

      <VehicleConflictDialog
        open={conflictDialogOpen}
        onClose={() => { setConflictDialogOpen(false); setPendingUpdate(null); setConflicts([]); }}
        conflicts={conflicts}
        targetVehicle={pendingUpdate?.updateData?.number}
        onReassign={handleConflictReassign}
        loading={saving}
      />
    </Box>
  );
};

export default DriverRegisterList;
